import { Descriptions, Flex, Tag, Typography } from 'antd';
import { FC } from 'react';

import { Schemas } from '@api-client/generated/types';

const { Text } = Typography;

/**
 * Why an account could not be checked, in words rather than in enum values. The same wording the
 * per-company account list uses, because it is the same verdict seen from further away.
 */
const REASONS: Record<string, string> = {
  no_booked_balance: 'bank publishes only an available balance',
  reference_date_unchanged: 'bank has not moved the date on its balance',
  balance_type_changed: 'bank changed which balance it publishes',
  balance_fetch_failed: 'bank refused the last balance requests',
  no_second_observation: 'only one observation so far',
  no_observations: 'no observations recorded yet',
};

type PortfolioSummaryProps = {
  summary: Schemas.BalancePortfolioSummaryDto;
};

/**
 * The denominators, which are the difference between this screen being useful and being misleading.
 *
 * Roughly seven in ten connected accounts publish only an available balance — Revolut and Wise are
 * two thirds of the portfolio — and an available balance nets card authorisation holds, which are not
 * booked entries, so it cannot be compared against the transactions at all. Showing "3 discrepancies"
 * on its own would be read as "3 problems in the whole estate"; showing "3 of 54 checked" without the
 * 314 would be read as an estate of 54. Both numbers have to be on the page, next to each other.
 *
 * "Not checked" carries its own reasons in the same cell, on a full-width row: the count and the
 * reasons are one fact, and a reader who sees the count without "because the bank publishes only an
 * available balance" has no way to tell a portfolio nobody can check from a sync that has stopped.
 */
const PortfolioSummary: FC<PortfolioSummaryProps> = ({ summary }) => (
  <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }} size="small">
    <Descriptions.Item label="Do not add up">
      <Text strong type={summary.mismatched > 0 ? 'danger' : undefined}>
        {summary.mismatched}
      </Text>
      {summary.mismatched > 0 && (
        <Text type="secondary">
          {' '}
          across {summary.companiesWithMismatch}{' '}
          {summary.companiesWithMismatch === 1 ? 'company' : 'companies'}
        </Text>
      )}
    </Descriptions.Item>

    <Descriptions.Item label="Checked">
      {summary.checked}
      <Text type="secondary"> of {summary.connectedAccounts} connected</Text>
    </Descriptions.Item>

    <Descriptions.Item label="Companies">{summary.companies}</Descriptions.Item>

    <Descriptions.Item label="Not checked" span={3}>
      <Flex gap={8} vertical>
        <Text>
          {summary.notReconcilable}
          <Text type="secondary"> — nothing was verified for these</Text>
        </Text>

        {summary.notReconcilableReasons.length > 0 && (
          <Flex align="center" gap={8} wrap="wrap">
            <Text type="secondary">because the</Text>
            {summary.notReconcilableReasons.map(({ reason, accounts }) => (
              <Tag key={reason}>
                {REASONS[reason] ?? reason} · {accounts}
              </Tag>
            ))}
          </Flex>
        )}
      </Flex>
    </Descriptions.Item>
  </Descriptions>
);

export default PortfolioSummary;
